// ---------------------------------------------------------------------------
// Tailwind className manipulation utilities for the property inspector.
//
// These helpers extract and replace specific Tailwind utility classes from
// a className string without destroying unrelated classes.
// ---------------------------------------------------------------------------

/** Prefixes that map to inspector property categories */
const BG_PREFIX = 'bg-'
const TEXT_COLOR_PREFIX = 'text-'
const FONT_SIZE_PREFIX = 'text-'
const FONT_WEIGHT_PREFIXES = [
  'font-thin', 'font-extralight', 'font-light', 'font-normal',
  'font-medium', 'font-semibold', 'font-bold', 'font-extrabold', 'font-black',
]
const ROUNDED_PREFIX = 'rounded'
const OPACITY_PREFIX = 'opacity-'
const GAP_PREFIX = 'gap-'
const P_PREFIXES = ['p-', 'px-', 'py-', 'pt-', 'pr-', 'pb-', 'pl-']
const M_PREFIXES = ['m-', 'mx-', 'my-', 'mt-', 'mr-', 'mb-', 'ml-']
const W_PREFIX = 'w-'
const H_PREFIX = 'h-'
const FLEX_DIRECTION_CLASSES = ['flex-row', 'flex-col', 'flex-row-reverse', 'flex-col-reverse']
const ITEMS_PREFIX = 'items-'
const JUSTIFY_PREFIX = 'justify-'
const FLEX_WRAP_CLASSES = ['flex-wrap', 'flex-nowrap', 'flex-wrap-reverse']

/** Tailwind color palette for dropdowns */
export const TAILWIND_COLORS = [
  'slate', 'gray', 'zinc', 'neutral', 'stone',
  'red', 'orange', 'amber', 'yellow', 'lime',
  'green', 'emerald', 'teal', 'cyan', 'sky',
  'blue', 'indigo', 'violet', 'purple', 'fuchsia',
  'pink', 'rose',
] as const

export const TAILWIND_SHADES = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const

/**
 * Replace all classes matching a prefix with a new class.
 * If newClass is empty, simply removes the matching classes.
 */
export function replaceClassByPrefix(
  className: string,
  prefix: string,
  newClass: string,
): string {
  const classes = className.split(/\s+/).filter(Boolean)
  const filtered = classes.filter((c) => !c.startsWith(prefix))
  if (newClass) filtered.push(newClass)
  return filtered.join(' ')
}

/**
 * Replace all classes that exactly match any value in `matches` with a new class.
 */
export function replaceClassExact(
  className: string,
  matches: string[],
  newClass: string,
): string {
  const classes = className.split(/\s+/).filter(Boolean)
  const filtered = classes.filter((c) => !matches.includes(c))
  if (newClass) filtered.push(newClass)
  return filtered.join(' ')
}

/**
 * Check if a class with the given prefix exists in className.
 */
export function hasClassWithPrefix(className: string, prefix: string): boolean {
  return className.split(/\s+/).some((c) => c.startsWith(prefix))
}

/**
 * Extract the first class matching a prefix.
 */
export function extractClass(className: string, prefix: string): string | undefined {
  return className.split(/\s+/).find((c) => c.startsWith(prefix))
}

/**
 * Extract background color class (e.g., 'bg-blue-500' -> 'blue-500')
 */
export function extractBgColor(className: string): string {
  const match = extractClass(className, BG_PREFIX)
  if (!match) return ''
  return match.slice(BG_PREFIX.length)
}

/**
 * Set background color class.
 */
export function setBgColor(className: string, colorValue: string): string {
  if (!colorValue) return replaceClassByPrefix(className, BG_PREFIX, '')
  return replaceClassByPrefix(className, BG_PREFIX, `bg-${colorValue}`)
}

/**
 * Extract text color from className.
 * Text color classes start with 'text-' but NOT font-size classes like 'text-sm', 'text-lg'.
 */
const TEXT_SIZE_VALUES = [
  'xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl', '9xl',
]

export function extractTextColor(className: string): string {
  const classes = className.split(/\s+/)
  const textClass = classes.find(
    (c) => c.startsWith(TEXT_COLOR_PREFIX) && !TEXT_SIZE_VALUES.includes(c.slice(TEXT_COLOR_PREFIX.length)),
  )
  if (!textClass) return ''
  return textClass.slice(TEXT_COLOR_PREFIX.length)
}

/**
 * Set text color class (careful not to remove text-size classes).
 */
export function setTextColor(className: string, colorValue: string): string {
  const classes = className.split(/\s+/).filter(Boolean)
  const filtered = classes.filter(
    (c) => !(c.startsWith(TEXT_COLOR_PREFIX) && !TEXT_SIZE_VALUES.includes(c.slice(TEXT_COLOR_PREFIX.length))),
  )
  if (colorValue) filtered.push(`text-${colorValue}`)
  return filtered.join(' ')
}

/**
 * Extract font size class (e.g., 'text-lg' -> 'lg')
 */
export function extractFontSize(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find(
    (c) => c.startsWith(FONT_SIZE_PREFIX) && TEXT_SIZE_VALUES.includes(c.slice(FONT_SIZE_PREFIX.length)),
  )
  if (!match) return ''
  return match.slice(FONT_SIZE_PREFIX.length)
}

/**
 * Set font size class.
 */
export function setFontSize(className: string, sizeValue: string): string {
  const classes = className.split(/\s+/).filter(Boolean)
  const filtered = classes.filter(
    (c) => !(c.startsWith(FONT_SIZE_PREFIX) && TEXT_SIZE_VALUES.includes(c.slice(FONT_SIZE_PREFIX.length))),
  )
  if (sizeValue) filtered.push(`text-${sizeValue}`)
  return filtered.join(' ')
}

/**
 * Extract font weight class.
 */
export function extractFontWeight(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find((c) => FONT_WEIGHT_PREFIXES.includes(c))
  if (!match) return ''
  return match.replace('font-', '')
}

/**
 * Set font weight class.
 */
export function setFontWeight(className: string, weight: string): string {
  if (!weight) return replaceClassExact(className, FONT_WEIGHT_PREFIXES, '')
  return replaceClassExact(className, FONT_WEIGHT_PREFIXES, `font-${weight}`)
}

/**
 * Extract border radius class.
 */
export function extractRounded(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find((c) => c === 'rounded' || c.startsWith('rounded-'))
  if (!match) return ''
  return match === 'rounded' ? 'DEFAULT' : match.replace('rounded-', '')
}

/**
 * Set border radius class.
 */
export function setRounded(className: string, value: string): string {
  const classes = className.split(/\s+/).filter(Boolean)
  const filtered = classes.filter((c) => c !== 'rounded' && !c.startsWith('rounded-'))
  if (value === 'DEFAULT') {
    filtered.push('rounded')
  } else if (value) {
    filtered.push(`rounded-${value}`)
  }
  return filtered.join(' ')
}

/**
 * Extract opacity value (0-100).
 */
export function extractOpacity(className: string): number | undefined {
  const match = extractClass(className, OPACITY_PREFIX)
  if (!match) return undefined
  const val = Number(match.slice(OPACITY_PREFIX.length))
  return Number.isNaN(val) ? undefined : val
}

/**
 * Set opacity class.
 */
export function setOpacity(className: string, value: number | undefined): string {
  if (value === undefined || value === 100) {
    return replaceClassByPrefix(className, OPACITY_PREFIX, '')
  }
  return replaceClassByPrefix(className, OPACITY_PREFIX, `opacity-${value}`)
}

/**
 * Extract gap value.
 */
export function extractGap(className: string): string {
  const match = extractClass(className, GAP_PREFIX)
  return match ? match.slice(GAP_PREFIX.length) : ''
}

/**
 * Set gap class.
 */
export function setGap(className: string, value: string): string {
  if (!value) return replaceClassByPrefix(className, GAP_PREFIX, '')
  return replaceClassByPrefix(className, GAP_PREFIX, `gap-${value}`)
}

/**
 * Check if className includes flex-wrap.
 */
export function hasFlexWrap(className: string): boolean {
  return className.split(/\s+/).includes('flex-wrap')
}

/**
 * Set or remove flex-wrap.
 */
export function setFlexWrap(className: string, wrap: boolean): string {
  const cleaned = replaceClassExact(className, FLEX_WRAP_CLASSES, '')
  if (wrap) return cleaned + ' flex-wrap'
  return cleaned
}

/**
 * Extract flex direction.
 */
export function extractFlexDirection(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find((c) => FLEX_DIRECTION_CLASSES.includes(c))
  return match ?? ''
}

/**
 * Set flex direction class.
 */
export function setFlexDirection(className: string, dir: string): string {
  if (!dir) return replaceClassExact(className, FLEX_DIRECTION_CLASSES, '')
  return replaceClassExact(className, FLEX_DIRECTION_CLASSES, dir)
}

/**
 * Extract items-* alignment.
 */
export function extractAlignItems(className: string): string {
  const match = extractClass(className, ITEMS_PREFIX)
  return match ? match.slice(ITEMS_PREFIX.length) : ''
}

/**
 * Set items-* class.
 */
export function setAlignItems(className: string, value: string): string {
  if (!value) return replaceClassByPrefix(className, ITEMS_PREFIX, '')
  return replaceClassByPrefix(className, ITEMS_PREFIX, `items-${value}`)
}

/**
 * Extract justify-* class.
 */
export function extractJustifyContent(className: string): string {
  const match = extractClass(className, JUSTIFY_PREFIX)
  return match ? match.slice(JUSTIFY_PREFIX.length) : ''
}

/**
 * Set justify-* class.
 */
export function setJustifyContent(className: string, value: string): string {
  if (!value) return replaceClassByPrefix(className, JUSTIFY_PREFIX, '')
  return replaceClassByPrefix(className, JUSTIFY_PREFIX, `justify-${value}`)
}

// ---------------------------------------------------------------------------
// Shadow utilities
// ---------------------------------------------------------------------------

const SHADOW_CLASSES = [
  'shadow-none', 'shadow-sm', 'shadow', 'shadow-md', 'shadow-lg', 'shadow-xl', 'shadow-2xl',
  'shadow-inner',
]

/**
 * Extract shadow value.
 * 'shadow' -> '', 'shadow-md' -> 'md', 'shadow-none' -> 'none'
 */
export function extractShadow(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find((c) => SHADOW_CLASSES.includes(c))
  if (!match) return ''
  if (match === 'shadow') return ''
  return match.replace('shadow-', '')
}

/**
 * Set shadow class.
 * '' -> 'shadow' (default), 'md' -> 'shadow-md', 'none' -> 'shadow-none'
 */
export function setShadow(className: string, value: string): string {
  const classes = className.split(/\s+/).filter(Boolean).filter((c) => !SHADOW_CLASSES.includes(c))
  if (value === '') {
    // empty string means default shadow
    classes.push('shadow')
  } else if (value) {
    classes.push(`shadow-${value}`)
  }
  return classes.join(' ')
}

// ---------------------------------------------------------------------------
// Border width utilities
// ---------------------------------------------------------------------------

const BORDER_WIDTH_CLASSES = ['border', 'border-0', 'border-2', 'border-4', 'border-8']

/**
 * Extract border width value.
 * 'border' -> '' (default 1px), 'border-2' -> '2', 'border-0' -> '0'
 */
export function extractBorderWidth(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find((c) => BORDER_WIDTH_CLASSES.includes(c))
  if (!match) return ''
  if (match === 'border') return ''
  return match.replace('border-', '')
}

/**
 * Set border width class.
 */
export function setBorderWidth(className: string, value: string): string {
  const classes = className.split(/\s+/).filter(Boolean).filter((c) => !BORDER_WIDTH_CLASSES.includes(c))
  if (value === '') {
    classes.push('border')
  } else if (value === '0') {
    classes.push('border-0')
  } else if (value) {
    classes.push(`border-${value}`)
  }
  return classes.join(' ')
}

// ---------------------------------------------------------------------------
// Border color utilities
// ---------------------------------------------------------------------------

const BORDER_COLOR_PREFIX = 'border-'
const BORDER_WIDTH_VALUES = ['0', '2', '4', '8']
const BORDER_STYLE_VALUES = ['solid', 'dashed', 'dotted', 'double', 'hidden', 'none']
// These border-* classes are NOT colors
const BORDER_NON_COLOR = [
  'border', 'border-0', 'border-2', 'border-4', 'border-8',
  'border-t', 'border-r', 'border-b', 'border-l',
  'border-x', 'border-y',
  ...BORDER_STYLE_VALUES.map((s) => `border-${s}`),
  'border-collapse', 'border-separate',
]

/**
 * Extract border color (e.g. 'border-blue-500' -> 'blue-500', 'border-white' -> 'white')
 */
export function extractBorderColor(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find((c) => {
    if (!c.startsWith(BORDER_COLOR_PREFIX)) return false
    if (BORDER_NON_COLOR.includes(c)) return false
    // Must not be a rounded class
    if (c.startsWith('border-t-') || c.startsWith('border-r-') || c.startsWith('border-b-') || c.startsWith('border-l-')) return false
    const rest = c.slice(BORDER_COLOR_PREFIX.length)
    if (BORDER_WIDTH_VALUES.includes(rest)) return false
    if (BORDER_STYLE_VALUES.includes(rest)) return false
    return true
  })
  if (!match) return ''
  return match.slice(BORDER_COLOR_PREFIX.length)
}

/**
 * Set border color class.
 */
export function setBorderColor(className: string, colorValue: string): string {
  const classes = className.split(/\s+/).filter(Boolean)
  const filtered = classes.filter((c) => {
    if (!c.startsWith(BORDER_COLOR_PREFIX)) return true
    if (BORDER_NON_COLOR.includes(c)) return true
    if (c.startsWith('border-t-') || c.startsWith('border-r-') || c.startsWith('border-b-') || c.startsWith('border-l-')) return true
    const rest = c.slice(BORDER_COLOR_PREFIX.length)
    if (BORDER_WIDTH_VALUES.includes(rest)) return true
    if (BORDER_STYLE_VALUES.includes(rest)) return true
    return false
  })
  if (colorValue) filtered.push(`border-${colorValue}`)
  return filtered.join(' ')
}

// ---------------------------------------------------------------------------
// Border style utilities
// ---------------------------------------------------------------------------

const BORDER_STYLE_CLASSES = BORDER_STYLE_VALUES.map((s) => `border-${s}`)

/**
 * Extract border style (e.g. 'border-dashed' -> 'dashed')
 */
export function extractBorderStyle(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find((c) => BORDER_STYLE_CLASSES.includes(c))
  if (!match) return ''
  return match.replace('border-', '')
}

/**
 * Set border style class.
 */
export function setBorderStyle(className: string, style: string): string {
  const classes = className.split(/\s+/).filter(Boolean).filter((c) => !BORDER_STYLE_CLASSES.includes(c))
  if (style) classes.push(`border-${style}`)
  return classes.join(' ')
}

// ---------------------------------------------------------------------------
// Line height (leading-*) utilities
// ---------------------------------------------------------------------------

const LEADING_PREFIX = 'leading-'
const LEADING_VALUES = ['none', 'tight', 'snug', 'normal', 'relaxed', 'loose', '3', '4', '5', '6', '7', '8', '9', '10']

/**
 * Extract leading (line-height) value.
 */
export function extractLeading(className: string): string {
  const match = extractClass(className, LEADING_PREFIX)
  if (!match) return ''
  return match.slice(LEADING_PREFIX.length)
}

/**
 * Set leading class.
 */
export function setLeading(className: string, value: string): string {
  if (!value) return replaceClassByPrefix(className, LEADING_PREFIX, '')
  return replaceClassByPrefix(className, LEADING_PREFIX, `leading-${value}`)
}

// ---------------------------------------------------------------------------
// Overflow utilities
// ---------------------------------------------------------------------------

const OVERFLOW_CLASSES = [
  'overflow-auto', 'overflow-hidden', 'overflow-visible', 'overflow-scroll',
  'overflow-x-auto', 'overflow-x-hidden', 'overflow-x-visible', 'overflow-x-scroll',
  'overflow-y-auto', 'overflow-y-hidden', 'overflow-y-visible', 'overflow-y-scroll',
]

/**
 * Extract overflow value (e.g. 'overflow-hidden' -> 'hidden')
 */
export function extractOverflow(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find((c) => OVERFLOW_CLASSES.includes(c))
  if (!match) return ''
  return match.replace('overflow-', '')
}

/**
 * Set overflow class.
 */
export function setOverflow(className: string, value: string): string {
  const classes = className.split(/\s+/).filter(Boolean).filter((c) => !OVERFLOW_CLASSES.includes(c))
  if (value) classes.push(`overflow-${value}`)
  return classes.join(' ')
}

// ---------------------------------------------------------------------------
// Position utilities
// ---------------------------------------------------------------------------

const POSITION_CLASSES = ['static', 'relative', 'absolute', 'fixed', 'sticky']

/**
 * Extract position value.
 */
export function extractPosition(className: string): string {
  const classes = className.split(/\s+/)
  const match = classes.find((c) => POSITION_CLASSES.includes(c))
  return match ?? ''
}

/**
 * Set position class.
 */
export function setPosition(className: string, value: string): string {
  const classes = className.split(/\s+/).filter(Boolean).filter((c) => !POSITION_CLASSES.includes(c))
  if (value) classes.push(value)
  return classes.join(' ')
}

// ---------------------------------------------------------------------------
// Width percent utilities — for proportional (bounded flex) sizing
// ---------------------------------------------------------------------------

/** Tailwind fraction classes and their percentage equivalents */
const FRACTION_MAP: [string, number][] = [
  ['w-full', 100],
  ['w-1/2', 50],
  ['w-1/3', 33.33],
  ['w-2/3', 66.67],
  ['w-1/4', 25],
  ['w-3/4', 75],
  ['w-1/5', 20],
  ['w-2/5', 40],
  ['w-3/5', 60],
  ['w-4/5', 80],
  ['w-1/6', 16.67],
  ['w-5/6', 83.33],
]

/** All fraction class names for filtering */
const FRACTION_CLASSES = FRACTION_MAP.map(([cls]) => cls)

/**
 * Extract width as a percentage from a className string.
 * Handles: 'w-1/2' → 50, 'w-[40%]' → 40, 'w-full' → 100
 * Returns undefined if no percentage-based width is found.
 */
export function extractWidthPercent(className: string): number | undefined {
  const classes = className.split(/\s+/)

  // Check arbitrary percent: w-[N%]
  const arbitrary = classes.find((c) => /^w-\[\d+(\.\d+)?%\]$/.test(c))
  if (arbitrary) {
    return parseFloat(arbitrary.slice(3, -2)) // strip 'w-[' and '%]'
  }

  // Check fraction classes
  for (const [cls, pct] of FRACTION_MAP) {
    if (classes.includes(cls)) return pct
  }

  return undefined
}

/**
 * Set width as a percentage in className.
 * Uses the closest Tailwind fraction if within 2%, otherwise arbitrary w-[N%].
 */
export function setWidthPercent(className: string, percent: number): string {
  // Remove existing width classes (both fraction and arbitrary percent)
  const classes = className.split(/\s+/).filter(Boolean).filter((c) => {
    if (FRACTION_CLASSES.includes(c)) return false
    if (/^w-\[\d+(\.\d+)?%\]$/.test(c)) return false
    if (c.startsWith('w-')) return false
    return true
  })

  // Find closest Tailwind fraction (within 2%)
  let bestClass: string | null = null
  let bestDiff = Infinity
  for (const [cls, pct] of FRACTION_MAP) {
    const diff = Math.abs(percent - pct)
    if (diff < bestDiff && diff <= 2) {
      bestDiff = diff
      bestClass = cls
    }
  }

  classes.push(bestClass ?? `w-[${Math.round(percent)}%]`)
  return classes.join(' ')
}

/**
 * Extract flex-basis value from className.
 * 'basis-1/2' → '1/2', 'basis-full' → 'full'
 */
export function extractFlexBasis(className: string): string | undefined {
  const match = extractClass(className, 'basis-')
  return match ? match.slice(6) : undefined
}

// Export prefix constants for external use
export {
  BG_PREFIX,
  TEXT_COLOR_PREFIX,
  FONT_SIZE_PREFIX,
  FONT_WEIGHT_PREFIXES,
  ROUNDED_PREFIX,
  OPACITY_PREFIX,
  GAP_PREFIX,
  P_PREFIXES,
  M_PREFIXES,
  W_PREFIX,
  H_PREFIX,
  ITEMS_PREFIX,
  JUSTIFY_PREFIX,
  TEXT_SIZE_VALUES,
  SHADOW_CLASSES,
  BORDER_WIDTH_CLASSES,
  BORDER_STYLE_CLASSES,
  LEADING_VALUES,
  OVERFLOW_CLASSES,
  POSITION_CLASSES,
  FRACTION_MAP,
}
